import { useMemo, useRef, useState } from 'react'

import { faBinoculars, faLock } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Box, Button, IconButton, useMediaQuery, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'
import { isEqual } from 'lodash'

import HSExploreViewMenu from './ViewsMenu'
import { HSExploreView } from './types'
import { useViews } from './useViews'

const areViewsEqual = (view1: HSExploreView, view2: HSExploreView) => {
  if (!view1 || !view2) return false
  const cleanFilterModel1 = view1.filterModel
    ? Object.fromEntries(Object.entries(view1.filterModel).filter((p) => p[1] !== undefined))
    : undefined

  const cleanFilterModel2 = view2.filterModel
    ? Object.fromEntries(Object.entries(view2.filterModel).filter((p) => p[1] !== undefined))
    : undefined

  return isEqual({ ...view1, filterModel: cleanFilterModel1 }, { ...view2, filterModel: cleanFilterModel2 })
}

export default function ViewsButton({ enabled, iconOnly }: { enabled: boolean; iconOnly?: boolean }) {
  return !enabled ? (
    <Badge
      color="secondary"
      badgeContent={<FontAwesomeIcon icon={faLock} />}
    >
      <ResponsiveViewsButton
        disabled
        iconOnly={iconOnly}
      />
    </Badge>
  ) : (
    <HSExploreViewsButton iconOnly={iconOnly} />
  )
}

const HSExploreViewsButton = ({ iconOnly }: { iconOnly?: boolean }) => {
  const [open, setOpen] = useState(false)
  const viewMenuAnchorEl = useRef(null)
  const { activeView, views } = useViews()
  const hasUnsavedChanges = useMemo(() => {
    const compareView = views.views.find((v) => v.id === activeView?.id)
    if (compareView) {
      if (!areViewsEqual(activeView, compareView)) {
        return true
      }
    }
  }, [activeView, views])

  return (
    <Box display="inline-block">
      <Badge
        invisible={!hasUnsavedChanges}
        variant="dot"
        color="primary"
      >
        <ResponsiveViewsButton
          activeView={activeView}
          setOpen={setOpen}
          anchorEl={viewMenuAnchorEl}
          iconOnly={iconOnly}
        />
      </Badge>

      <HSExploreViewMenu
        anchorEl={viewMenuAnchorEl.current}
        hasUnsavedChanges={hasUnsavedChanges}
        open={open}
        onClose={() => setOpen(false)}
      />
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  iconButton: {
    '&.MuiIconButton-root.Mui-disabled': {
      color: theme.palette.primary.main,
      opacity: 0.3,
    },
  },
  viewName: {
    maxWidth: '150px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
}))
type ResponsiveViewsButtonProps = {
  disabled?: boolean
  activeView?: { name: string }
  setOpen?: (open: boolean) => void
  anchorEl?: React.RefObject<HTMLButtonElement>
  iconOnly?: boolean
}
const ResponsiveViewsButton = ({ disabled, activeView, setOpen, anchorEl, iconOnly }: ResponsiveViewsButtonProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))
  return isMediumScreenSize || iconOnly ? (
    <Tooltip title={activeView?.name ?? 'Views'}>
      <IconButton
        color="primary"
        className={classes.iconButton}
        disabled={disabled}
        ref={anchorEl}
        onClick={() => setOpen?.(true)}
      >
        <FontAwesomeIcon icon={faBinoculars} />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      disabled={disabled}
      ref={anchorEl}
      color="primary"
      variant="outlined"
      startIcon={<FontAwesomeIcon icon={faBinoculars} />}
      onClick={() => setOpen?.(true)}
    >
      <span className={classes.viewName}>{activeView?.name ?? 'Views'}</span>
    </Button>
  )
}
